import { Card, Col, Divider, Form, Row, Select, Space } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import ButtonAction from 'components/Common/ButtonAction'
import {
    ButtonPrint,
    ButtonExcel
} from 'components/Common/Buttons'
import { ButtonAdvanceFilter } from 'components/Common/ButtonAdvanceFilter'
import CustomAntdTable from 'components/Common/CustomAntdTable'
import SkeletonPage from 'components/Common/SkeletonPage'
import BaseAdvanceFilter from 'components/CustomDrawer/BaseAdvanceFilter'
import CellTable from 'components/Common/CellTable'
import Error403 from 'components/Error/Error403'

import useTopbarTitleHooks from 'hooks/useTopbarTitleHooks'
import useIsMobileHooks from 'hooks/useIsMobileHooks'
import useOrgRepo from 'services/repo/useOrgRepo'
import useReportRepo from 'services/repo/useReportRepo'

import Constant from 'utils/Constants'
import { sorterTable } from 'utils/Helper'
import {
    dismissToast,
    errorToast,
    loadingToastId,
    successToast
} from 'utils/Toast'

import { PRIVILEGE_ID } from 'utils/AccessPermissionConfig'
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant'
import useDownloadReportOrganizationRepo from 'services/repo/useDownloadReportRepo'

export default function OrganizationReport() {
    useTopbarTitleHooks("Report Organisasi");
    const isMobile = useIsMobileHooks();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    // Repo
    const { isLoading: isLoadingOrgList, getOrgList } = useOrgRepo(false);
    const { isLoading: isLoadingReport, getReportOrgList } = useReportRepo(false);
    const { isLoading: isLoadingDownloadReportOrganization, getDownloadExcelOrganizationReport } = useDownloadReportOrganizationRepo(false);

    // Privilege
    const isLoadingPrivilege = useRef(true);
    const hasPrivilegePage = useRef(true);
    const [isLoadedPrivilege, setIsLoadedPrivilege] = useState(false);
    const [showButtonAdd, setShowButtonAdd] = useState(false)


    // Table & Filter
    const [data, setData] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [isOpenAdvanceFilter, setIsOpenAdvanceFilter] = useState(false);

    const [filters, setFilters] = useState({ orgId: null });
    const [tableParams, setTableParams] = useState({
        pageNumber: 1,
        pageSize: 10,
        filter: { ...filters },
        orderBy: "orgName",
        isAscending: true
    });

    // === Handlers ===
    const onSearch = () => {
        const { orgId } = form.getFieldsValue();
        const selectedOrgId = orgId?.value ?? orgId ?? null;

        setTableParams(prev => ({
            ...prev,
            pageNumber: 1,
            filter: {
                ...filters,
                orgId: selectedOrgId
            }
        }));
    };

    const onSearchClick = () => {
        onSearch()
        setIsOpenAdvanceFilter(false)
    }

    const handleTableChange = (pagination, filters, sorter) => {
        setTableParams(prevParams => ({
            ...prevParams,
            pageNumber: pagination?.current,
            pageSize: pagination?.pageSize,
            orderBy: sorter?.field || prevParams?.orderBy,
            isAscending: sorterTable(sorter?.order, prevParams?.isAscending)
        }));
        if (pagination.pageSize !== tableParams?.pageSize) {
            setData([]);
        }
    };

    const handleView = async (data) => {
        const key = 'loadingEdit';
        loadingToastId(Constant.message.loading, key);

        // GUNAKAN FUNGSI YANG SUDAH BENAR
        const targetPath = APP_ROUTE_PREFIX + "/" + RouteConstant.report.reportDivision(data?.orgId);
        navigate(targetPath);

        dismissToast(key);
    };

    const handleExport = async () => {
        const key = "loadingExportOrg";
        loadingToastId("Exporting file...", key);

        try {
            // Ambil orgId dari filter atau form
            const formOrg = form.getFieldValue("orgId");
            const selectedOrgId = (formOrg?.value ?? formOrg ?? tableParams?.filter?.orgId) || null;

            // Call repo
            const response = await getDownloadExcelOrganizationReport({ orgId: selectedOrgId });

            // Ambil nama file dari Content-Disposition
            const dispo = response.headers["content-disposition"];
            let fileName = `${getCurrentDateTime()}-Report Organization.xlsx`;

            if (dispo) {
                const match = dispo.match(/filename\*=UTF-8''(.+)|filename="?([^"]+)"?/i);
                if (match) {
                    fileName = decodeURIComponent(match[1] || match[2]);
                }
            }

            // Buat blob
            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            // Trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            dismissToast(key);
            successToast("File successfully downloaded.");
        } catch (error) {
            dismissToast(key);
            errorToast("Export failed.");
            console.error("Export failed:", error);
            if (error?.response) {
                console.error("📡 Status:", error.response.status);
                console.error("📄 Data:", error.response.data);
            }
        }
    };


    const getCurrentDateTime = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}${month}${day}${hours}${minutes}${seconds}`;
    };

    const fetchCheckPrivilege = async () => {
        setShowButtonAdd(false)
        isLoadingPrivilege.current = false
        hasPrivilegePage.current = true;
        setIsLoadedPrivilege(true)
        // await fetchData();
        dismissToast(PRIVILEGE_ID.AddEditDivision);
    }

    const fetchData = async () => {
        try {
            const res = await getReportOrgList({ ...tableParams });
            if (res?.detail === 'OK' && res?.status === 200) {
                setData(res.data.content);
                setTotalData(res.data.totalElements);
            }
        } finally {
        }
    };

    // Org dropdown
    const [orgOptions, setOrgOptions] = useState([])
    const fetchDataOrg = async () => {
        const payload = {
            pageNumber: 1,
            pageSize: 0,
            orderBy: "orgId",
            isAscending: true,
            filter: {
                ...filters
            }
        };

        const res = await getOrgList(payload);

        if (res?.detail === 'OK' && res?.status === 200) {
            const options = res.data.content.map(item => ({
                label: item?.orgName,
                value: item?.orgId,
            }));

            setOrgOptions(options);

            if (options.length === 1) {
                const onlyOption = options[0];

                form.setFieldsValue({ orgId: onlyOption }); // isinya harus { label, value }
                setFilters((prev) => ({ ...prev, orgId: onlyOption.value }));

                setTableParams((prev) => ({
                    ...prev,
                    filter: {
                        ...prev.filter,
                        orgId: onlyOption.value
                    }
                }));
            }
        }
    };

    // === Effects ===

    useEffect(() => {
        fetchCheckPrivilege()
    }, [])


    useEffect(() => {
        if (isLoadedPrivilege) {
            fetchData();
        }

        fetchDataOrg();
    }, [JSON.stringify(tableParams)]);

    useEffect(() => {
        const timeOutId = setTimeout(() => {
            if (isLoadedPrivilege && !isMobile) {
                onSearch()
            }
        }, Constant.delayFilter);
        return () => clearTimeout(timeOutId);
    }, [filters]);


    // === Filter UI ===
    const filterUI = () => {
        return (
            <Form
                layout="horizontal"
                form={form}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={10} md={8}>
                        <Form.Item name='orgId' label="Filter By">
                            <Select
                                style={{ width: '100%' }}
                                allowClear
                                showSearch
                                placeholder='Select Organization'
                                loading={isLoadingOrgList}
                                options={orgOptions}
                                onChange={(selectedValue) => {
                                    const orgId = selectedValue ?? null;
                                    form.setFieldsValue({ orgId: orgId });
                                    setTableParams((prev) => ({
                                        ...prev,
                                        pageNumber: 1,
                                        filter: {
                                            ...prev.filter,
                                            orgId: orgId
                                        }
                                    }));
                                }}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        )
    }

    const columns = [
        {
            title: 'Action',
            key: 'action',
            width: "50px",
            render: (_, record) => {
                const buttons = showButtonAdd ? ['view', 'view'] : ['view']
                return (
                    <ButtonAction
                        buttons={['view']}
                        onView={() => handleView(record)}
                        data={record}
                    />
                )
            }
        },
        {
            title: 'Organization',
            dataIndex: 'orgName',
            sorter: true,
            width: "250px",
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Jumlah Divisi',
            dataIndex: 'divTotal',
            sorter: true,
            width: "140px",
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Jumlah Departement',
            dataIndex: 'deptTotal',
            width: "170px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Jumlah Posisi',
            dataIndex: 'posTotal',
            width: "120px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Total FTE',
            dataIndex: 'wbsContentFte',
            width: "120px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Rasio FTE',
            dataIndex: 'wbsRatio',
            width: "120px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Jumlah SDM',
            dataIndex: 'resourceTotal',
            width: "120px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Jumlah SDM Direncanakan',
            dataIndex: 'resourceTotalRecommendation',
            width: "140px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        },
        {
            title: 'Gap',
            dataIndex: 'resourceTotalGap',
            width: "100px",
            sorter: true,
            render: (data) => <CellTable data={data} />
        }

    ];

    // === Render ===
    return (
        <>
            {hasPrivilegePage.current ?
                <Space direction="vertical" size="large" className='space-vertical-flex'>
                    <Card size="big" bordered={false}>
                        {isLoadingPrivilege.current ? <SkeletonPage /> :
                            <>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={20} xl={18}>
                                       
                                    </Col>
                                </Row>
                                <Divider dashed className='mb-1 mt-1' />
                                {isMobile ?
                                    <ButtonAdvanceFilter onClick={() => setIsOpenAdvanceFilter(true)} />
                                    :
                                    (
                                        <>
                                            {filterUI()}
                                        </>)
                                }

                                <CustomAntdTable
                                    loading={isLoadingReport}
                                    columns={columns}
                                    dataSource={data}
                                    rowKey={"orgId"}
                                    tableParam={tableParams}
                                    totalData={totalData}
                                    onChange={handleTableChange}
                                // rowClick={handleRowClick}
                                // isOpenDrawer={isOpenDrawer}
                                // idDataHighlight={idHighlightRow}
                                />

                                <Space className="mb-2">
                                    <div>
                                    </div>
                                </Space>

                                <Col span={24} offset={10}>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <ButtonExcel buttonText="Download" onClick={handleExport} />

                                    </div>
                                </Col>
                            </>
                        }
                    </Card>
                </Space> : (<Error403 />)
            }

            {isOpenAdvanceFilter &&
                <BaseAdvanceFilter
                    onClose={() => setIsOpenAdvanceFilter(false)}
                    onSearchClick={onSearchClick}>
                    {filterUI()}
                </BaseAdvanceFilter>}
        </>
    )
}
